import React, { useState, useEffect, useMemo } from 'react';
import { AlertTriangle, Download, X, Shield, Plus, Edit3, Trash2, CheckSquare, Square, Search, Eye } from 'lucide-react';
import { api } from '../api/client';
import { useFilters } from '../context/FilterContext';
import { useAuth } from '../context/AuthContext';
import IssueDrawer from '../components/IssueDrawer';
import TrackingItemDetailDrawer from '../components/TrackingItemDetailDrawer';
import { ENTITY_COLORS } from '../components/EntityPicker';
import TrackingFilterBar from '../components/filters/TrackingFilterBar';

const IssuesPage = () => {
  const { filters: globalFilters, getFilterParams } = useFilters();
  const { user } = useAuth();
  const [issues, setIssues] = useState([]);
  const [escalationCounts, setEscalationCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState(null);
  
  // Filter Bar 2.0 state
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [quickFilters, setQuickFilters] = useState({});
  const [filterCounts, setFilterCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [createType, setCreateType] = useState('issue'); // 'issue' or 'risk'
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // Detail drawer with comments
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [viewingIssue, setViewingIssue] = useState(null);

  // Load issues when filters change
  useEffect(() => { loadIssues(); }, [advancedFilters, quickFilters, searchTerm]);

  const loadIssues = async () => {
    try {
      setLoading(true);
      
      // Build params for the new filter API
      const params = {
        // Multi-select filters
        ...(advancedFilters.status?.length > 0 && { status: advancedFilters.status.join(',') }),
        ...(advancedFilters.severity?.length > 0 && { severity: advancedFilters.severity.join(',') }),
        ...(advancedFilters.priority?.length > 0 && { priority: advancedFilters.priority.join(',') }),
        ...(advancedFilters.issue_type?.length > 0 && { issue_type: advancedFilters.issue_type.join(',') }),
        ...(advancedFilters.escalation_level?.length > 0 && { escalation_level: advancedFilters.escalation_level.join(',') }),
        ...(advancedFilters.owner?.length > 0 && { owner: advancedFilters.owner.join(',') }),
        ...(advancedFilters.portfolio?.length > 0 && { portfolio: advancedFilters.portfolio.join(',') }),
        ...(advancedFilters.program?.length > 0 && { program: advancedFilters.program.join(',') }),
        ...(advancedFilters.project?.length > 0 && { project: advancedFilters.project.join(',') }),
        
        // Date ranges
        ...(advancedFilters.due_date_start && { due_date_start: advancedFilters.due_date_start }),
        ...(advancedFilters.due_date_end && { due_date_end: advancedFilters.due_date_end }),
        ...(advancedFilters.raised_date_start && { raised_date_start: advancedFilters.raised_date_start }),
        ...(advancedFilters.raised_date_end && { raised_date_end: advancedFilters.raised_date_end }),
        
        // Quick filters
        ...quickFilters,
        
        // Search
        ...(searchTerm && { search: searchTerm }),
        
        // Current user for "my items" filter
        ...(user?.email && { current_user: user.email }),
      };
      
      const res = await api.filterIssues(params);
      setIssues(res.data.issues || []);
      setFilterCounts(res.data.counts || {});
      
      // Calculate escalation counts from the data
      const counts = {};
      (res.data.issues || []).forEach(issue => {
        const level = issue.escalation_level || 'Project';
        counts[level] = (counts[level] || 0) + 1;
      });
      setEscalationCounts(counts);
    } catch (error) { 
      console.error('Failed to load issues:', error); 
      // Fallback to governance API
      try {
        const res = await api.listIssues({});
        setIssues(res.data?.data?.issues || []);
      } catch (e) {
        setIssues([]);
      }
    }
    finally { setLoading(false); }
  };

  const handleCreateNew = (type = 'issue') => {
    setCreateType(type);
    setEditingIssue(null);
    setDrawerOpen(true);
  };

  const handleEdit = (issue) => {
    setEditingIssue(issue);
    setCreateType(issue.item_type || 'issue');
    setDrawerOpen(true);
  };

  const handleDrawerSuccess = () => {
    loadIssues();
    setSelectedIssue(null);
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === issues.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(issues.map(i => i.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length || !window.confirm(`Delete ${selectedIds.length} selected items?`)) return;
    setBulkLoading(true);
    try {
      await api.bulkDeleteIssues(selectedIds);
      setSelectedIds([]);
      loadIssues();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      alert('Failed to delete items');
    }
    setBulkLoading(false);
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (!selectedIds.length) return;
    setBulkLoading(true);
    try {
      await api.bulkUpdateIssuesStatus(selectedIds, newStatus);
      setSelectedIds([]);
      loadIssues();
    } catch (error) {
      console.error('Bulk status update failed:', error);
      alert('Failed to update status');
    }
    setBulkLoading(false);
  };

  const handleBulkPriorityUpdate = async (newPriority) => {
    if (!selectedIds.length) return;
    setBulkLoading(true);
    try {
      await api.bulkUpdateIssuesPriority(selectedIds, newPriority);
      setSelectedIds([]);
      loadIssues();
    } catch (error) {
      console.error('Bulk priority update failed:', error);
      alert('Failed to update priority');
    }
    setBulkLoading(false);
  };

  const getPriorityColor = (priority) => {
    const colors = { 'High': 'bg-red-100 text-red-800', 'Medium': 'bg-yellow-100 text-yellow-800', 'Low': 'bg-green-100 text-green-800' };
    return colors[priority] || 'bg-[var(--clr-surface-subtle)] text-gray-800';
  };

  const getStatusColor = (status) => {
    const colors = { 'Open': 'bg-[var(--clr-sev-blocked)]', 'In Progress': 'bg-[var(--clr-brand-primary)] bg-opacity-100', 'Closed': 'bg-[var(--clr-sev-healthy)]', 'Mitigated': 'bg-teal-500' };
    return colors[status] || 'bg-[var(--clr-surface-subtle)]0';
  };

  const getEscalationColor = (level) => {
    const colors = {
      'Executive/SteerCo': 'bg-red-100 text-red-700 border-red-200',
      'Portfolio': 'bg-orange-100 text-orange-700 border-orange-200',
      'Program': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'Project': 'bg-[var(--clr-surface-subtle)] text-gray-700 border-gray-200'
    };
    return colors[level] || 'bg-[var(--clr-surface-subtle)] text-gray-700 border-gray-200';
  };

  const kpis = {
    total: issues.length,
    open: issues.filter(i => i.status === 'Open').length,
    high: issues.filter(i => i.priority === 'High').length,
    avgRisk: issues.length > 0 ? (issues.reduce((s, i) => s + (i.risk_score || 0), 0) / issues.length).toFixed(1) : 0,
    steerco: escalationCounts['Executive/SteerCo'] || 0
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-[var(--clr-sev-atrisk)]" />
            Issues & Risks
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Track and manage all issues and risks across portfolios</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleCreateNew('issue')}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
            data-testid="new-issue-btn"
          >
            <Plus className="w-4 h-4" /> New Issue
          </button>
          <button
            onClick={() => handleCreateNew('risk')}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
            data-testid="new-risk-btn"
          >
            <AlertTriangle className="w-4 h-4" /> New Risk
          </button>
          <a href={api.exportCSV('issues', advancedFilters)} className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>

      {/* Filter Bar 2.0 */}
      <TrackingFilterBar
        trackingType="issues"
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        quickFilters={quickFilters}
        onQuickFiltersChange={setQuickFilters}
        counts={filterCounts}
        currentUser={user?.email}
        showInsightsBar={true}
        className="mb-6"
      />

      {/* Search Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search issues by title, description, or key..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
            data-testid="issues-search"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Total Issues</p><p className="text-2xl font-bold">{kpis.total}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Open</p><p className="text-2xl font-bold text-[var(--clr-sev-blocked)]">{kpis.open}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">High Severity</p><p className="text-2xl font-bold text-orange-600">{filterCounts.high_severity || 0}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4"><p className="text-sm text-[var(--clr-text-secondary)] mb-1">Overdue</p><p className="text-2xl font-bold text-[var(--clr-sev-blocked)]">{filterCounts.overdue || 0}</p></div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4 border-red-200">
          <p className="text-sm text-[var(--clr-sev-blocked)] mb-1 flex items-center gap-1"><Shield className="w-3 h-3" />SteerCo Escalations</p>
          <p className="text-2xl font-bold text-red-700">{kpis.steerco}</p>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedIds.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium text-blue-800">
            {selectedIds.length} item{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <select
              onChange={(e) => e.target.value && handleBulkStatusUpdate(e.target.value)}
              disabled={bulkLoading}
              className="text-sm border rounded px-2 py-1"
              data-testid="bulk-status-select"
            >
              <option value="">Set Status...</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Mitigated">Mitigated</option>
              <option value="Closed">Closed</option>
            </select>
            <select
              onChange={(e) => e.target.value && handleBulkPriorityUpdate(e.target.value)}
              disabled={bulkLoading}
              className="text-sm border rounded px-2 py-1"
              data-testid="bulk-priority-select"
            >
              <option value="">Set Priority...</option>
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
              <option value="Critical">Critical</option>
            </select>
            <button
              onClick={handleBulkDelete}
              disabled={bulkLoading}
              className="flex items-center gap-1 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4" /> Delete
            </button>
            <button
              onClick={() => setSelectedIds([])}
              className="text-sm text-gray-600 hover:text-gray-800 px-2"
              data-testid="bulk-clear-btn"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="bg-[var(--clr-surface-card)] rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div></div>
        ) : issues.length === 0 ? (
          <div className="text-center py-12"><AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-[var(--clr-text-secondary)]">No issues found</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--clr-surface-subtle)] border-b">
                <tr>
                  <th className="text-left px-3 py-3 w-10">
                    <button onClick={toggleSelectAll} className="p-1 hover:bg-gray-200 rounded" data-testid="select-all-issues">
                      {selectedIds.length === issues.length && issues.length > 0 ? (
                        <CheckSquare className="w-4 h-4 text-[var(--clr-brand-primary)]" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Issue Key</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Title</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Escalation</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Priority</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Risk Score</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Linked</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Owner</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {issues.map((i) => (
                  <tr key={i.id} onClick={() => setSelectedIssue(i)} className={`hover:bg-[var(--clr-surface-hover)] cursor-pointer ${selectedIds.includes(i.id) ? 'bg-blue-50' : ''}`} data-testid={`issue-row-${i.id}`}>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => toggleSelect(i.id)} className="p-1 hover:bg-gray-200 rounded" data-testid={`select-issue-${i.id}`}>
                        {selectedIds.includes(i.id) ? (
                          <CheckSquare className="w-4 h-4 text-[var(--clr-brand-primary)]" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-[var(--clr-brand-primary)]">{i.issue_key || '-'}</td>
                    <td className="px-4 py-3"><div className="font-medium text-[var(--clr-text-primary)] max-w-xs truncate">{i.title}</div></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${getEscalationColor(i.escalation_level)}`}>
                        {i.escalation_level || 'Project'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{i.type || i.item_type || '-'}</td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(i.priority)}`}>{i.priority || '-'}</span></td>
                    <td className="px-4 py-3"><span className={`text-sm font-bold ${i.risk_score >= 7 ? 'text-[var(--clr-sev-blocked)]' : i.risk_score >= 4 ? 'text-[var(--clr-sev-atrisk)]' : 'text-[var(--clr-sev-healthy)]'}`}>{i.risk_score?.toFixed ? i.risk_score.toFixed(1) : (i.risk_score || '-')}</span></td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium text-white ${getStatusColor(i.status)}`}>{i.status || '-'}</span></td>
                    <td className="px-4 py-3">
                      {i.links && i.links.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {i.links.slice(0, 2).map((link, idx) => (
                            <span 
                              key={idx} 
                              className={`px-1.5 py-0.5 text-xs rounded ${ENTITY_COLORS[link.entity_type] || 'bg-gray-100 text-gray-700'}`}
                              title={`${link.entity_type}: ${link.display}`}
                            >
                              {link.entity_type?.slice(0,3)}
                            </span>
                          ))}
                          {i.links.length > 2 && (
                            <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                              +{i.links.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--clr-text-muted)]">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{i.owner || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); setViewingIssue(i); setDetailDrawerOpen(true); }}
                          className="p-1.5 hover:bg-gray-100 rounded text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)]"
                          data-testid={`view-issue-${i.id}`}
                          title="View Details & Comments"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(i); }}
                          className="p-1.5 hover:bg-gray-100 rounded text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)]"
                          data-testid={`edit-issue-${i.id}`}
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedIssue && (
        <div className="fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedIssue(null)}></div>
          <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">{selectedIssue.title}</h2>
                <p className="text-sm text-[var(--clr-text-muted)]">{selectedIssue.issue_key}</p>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => { setSelectedIssue(null); handleEdit(selectedIssue); }}
                  className="px-3 py-1.5 bg-[var(--clr-brand-primary)] text-white rounded-lg text-sm hover:bg-[var(--clr-brand-primary-hover)] flex items-center gap-1"
                  data-testid="edit-from-drawer"
                >
                  <Edit3 className="w-4 h-4" /> Edit
                </button>
                <button onClick={() => setSelectedIssue(null)} className="p-2 hover:bg-[var(--clr-surface-hover)] rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Escalation Level Banner */}
              <div className={`rounded-lg p-3 border ${getEscalationColor(selectedIssue.escalation_level)}`}>
                <p className="text-xs font-semibold mb-1">Escalation Level</p>
                <p className="font-bold text-lg">{selectedIssue.escalation_level || 'Project'}</p>
              </div>
              
              {/* Linked Entities */}
              {selectedIssue.links && selectedIssue.links.length > 0 && (
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)] mb-2">Linked Entities</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedIssue.links.map((link, idx) => (
                      <span 
                        key={idx}
                        className={`px-2 py-1 text-xs rounded-lg border ${ENTITY_COLORS[link.entity_type] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                      >
                        <span className="font-medium capitalize">{link.entity_type}:</span> {link.display}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3"><p className="text-xs text-[var(--clr-text-muted)] mb-1">Type</p><p className="font-medium">{selectedIssue.type || selectedIssue.item_type || '-'}</p></div>
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3"><p className="text-xs text-[var(--clr-text-muted)] mb-1">Priority</p><p className={`font-medium ${selectedIssue.priority === 'High' ? 'text-[var(--clr-sev-blocked)]' : ''}`}>{selectedIssue.priority || '-'}</p></div>
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3"><p className="text-xs text-[var(--clr-text-muted)] mb-1">Likelihood</p><p className="font-medium">{selectedIssue.likelihood || '-'}</p></div>
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3"><p className="text-xs text-[var(--clr-text-muted)] mb-1">Impact</p><p className="font-medium">{selectedIssue.impact || '-'}</p></div>
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3"><p className="text-xs text-[var(--clr-text-muted)] mb-1">Risk Score</p><p className="font-bold text-lg">{selectedIssue.risk_score?.toFixed ? selectedIssue.risk_score.toFixed(1) : (selectedIssue.risk_score || '-')}</p></div>
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3"><p className="text-xs text-[var(--clr-text-muted)] mb-1">Status</p><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium text-white ${getStatusColor(selectedIssue.status)}`}>{selectedIssue.status}</span></div>
              </div>
              <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3"><p className="text-xs text-[var(--clr-text-muted)] mb-1">Owner</p><p className="font-medium">{selectedIssue.owner || '-'}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3"><p className="text-xs text-[var(--clr-text-muted)] mb-1">Date Reported</p><p className="font-medium">{selectedIssue.date_reported || selectedIssue.raised_date?.split('T')[0] || '-'}</p></div>
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3"><p className="text-xs text-[var(--clr-text-muted)] mb-1">Due Date</p><p className="font-medium">{selectedIssue.due_date?.split('T')[0] || '-'}</p></div>
              </div>
              <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3"><p className="text-xs text-[var(--clr-text-muted)] mb-1">Project</p><p className="font-medium">{selectedIssue.project_name || '-'}</p><p className="text-xs text-[var(--clr-text-muted)]">{selectedIssue.program_name} • {selectedIssue.portfolio_name}</p></div>
              {selectedIssue.description && <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3"><p className="text-xs text-[var(--clr-text-muted)] mb-1">Description</p><p className="text-sm">{selectedIssue.description}</p></div>}
              {selectedIssue.mitigation && <div className="bg-[var(--clr-sev-atrisk)] bg-opacity-10 rounded-lg p-3"><p className="text-xs text-[var(--clr-sev-atrisk)] mb-1">Mitigation</p><p className="text-sm">{selectedIssue.mitigation}</p></div>}
              {selectedIssue.resolution && <div className="bg-[var(--clr-sev-healthy)] bg-opacity-10 rounded-lg p-3"><p className="text-xs text-[var(--clr-sev-healthy)] mb-1">Resolution</p><p className="text-sm">{selectedIssue.resolution}</p></div>}
            </div>
          </div>
        </div>
      )}

      {/* Issue/Risk Create/Edit Drawer */}
      <IssueDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        issue={editingIssue}
        itemType={createType}
        onSuccess={handleDrawerSuccess}
      />

      {/* Detail Drawer with Comments */}
      <TrackingItemDetailDrawer
        isOpen={detailDrawerOpen}
        onClose={() => { setDetailDrawerOpen(false); setViewingIssue(null); }}
        item={viewingIssue}
        itemType={viewingIssue?.item_type || 'issue'}
        tenantId={user?.tenant?.id || user?.tenant_id}
      />
    </div>
  );
};

export default IssuesPage;
